import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Mail, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const CATEGORIES = [
  { key: "marketing", label: "Marketing emails" },
  { key: "product_updates", label: "Product updates" },
  { key: "newsletter", label: "Monthly newsletter" },
  { key: "nurture", label: "Educational content" },
];

export default function Unsubscribe() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    if (!token) { setState({ status: "invalid" }); return; }
    api.get(`/comms/unsubscribe/verify?token=${encodeURIComponent(token)}`)
      .then(r => setState({ status: "ready", email: r.data.email, category: r.data.category }))
      .catch(() => setState({ status: "invalid" }));
  }, [token]);

  const unsub = async (category) => {
    setState(s => ({ ...s, status: "saving" }));
    try {
      await api.post("/comms/unsubscribe/confirm", { token, category });
      setState(s => ({ ...s, status: "done", category }));
    } catch {
      setState(s => ({ ...s, status: "error" }));
    }
  };

  return (
    <div className="min-h-screen bg-[#F6F9FC] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="unsubscribe-page">
        <div className="bg-[#0A2540] px-8 py-6">
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">Instabridge</span>
            <span className="text-slate-300 text-lg">Funding</span>
          </div>
        </div>
        <div className="px-8 py-8">
          {state.status === "loading" && (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
          )}
          {state.status === "invalid" && (
            <div>
              <XCircle className="w-10 h-10 text-red-500 mb-3" />
              <h1 className="text-xl font-semibold text-[#0A2540] mb-2">Link expired</h1>
              <p className="text-sm text-slate-600">This unsubscribe link is invalid or has expired. Reply to any recent email from us and we'll update your preferences manually.</p>
            </div>
          )}
          {state.status === "ready" && (
            <div>
              <Mail className="w-10 h-10 text-[#0066FF] mb-3" />
              <h1 className="text-xl font-semibold text-[#0A2540] mb-2">Manage your email preferences</h1>
              <p className="text-sm text-slate-600 mb-6">Signed in as <strong>{state.email}</strong></p>
              <div className="space-y-2 mb-6">
                {CATEGORIES.map(c => (
                  <button key={c.key} onClick={() => unsub(c.key)}
                    className="w-full flex items-center justify-between px-4 py-3 border border-slate-200 rounded-lg hover:border-red-300 hover:bg-red-50 text-left transition"
                    data-testid={`unsub-${c.key}`}>
                    <span className="text-sm text-slate-900">{c.label}</span>
                    <span className="text-xs text-red-600 font-medium">Unsubscribe</span>
                  </button>
                ))}
              </div>
              <button onClick={() => unsub("all")}
                className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium rounded-lg"
                data-testid="unsub-all">
                Unsubscribe from everything
              </button>
              <p className="text-xs text-slate-400 mt-4">You'll still receive essential transactional emails about your active loans.</p>
            </div>
          )}
          {state.status === "done" && (
            <div>
              <CheckCircle2 className="w-10 h-10 text-green-500 mb-3" />
              <h1 className="text-xl font-semibold text-[#0A2540] mb-2">You're unsubscribed</h1>
              <p className="text-sm text-slate-600">You'll no longer receive <strong>{state.category === "all" ? "any marketing emails" : state.category.replace(/_/g, " ")}</strong> from Instabridge Funding.</p>
              <p className="text-xs text-slate-400 mt-4">Changed your mind? Reply to any email and we'll re-subscribe you.</p>
            </div>
          )}
          {state.status === "saving" && <div className="text-sm text-slate-500">Saving…</div>}
          {state.status === "error" && <div className="text-sm text-red-600">Something went wrong. Please try again.</div>}
        </div>
      </div>
    </div>
  );
}
